import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateSsoProviderBodyDto, SsoProviderService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { isValidEmail } from '@pages/Anonymous/LoginPage/components/Content/helpers/isValidEmail';
import { adminOrganizationsKeys } from 'app/queries/adminOrganizations/adminOrganizationsKeys';
import { ssoProvidersKeys } from 'app/queries/ssoProviders/ssoProvidersKeys';
import React, { useMemo, useState } from 'react';
import { createFetchOrganizationOptionsQueryFn } from './api/fetchOrganizationOptions';
import { createFetchAdminOrganizationTeamsQueryFn } from 'app/queries/adminOrganizations/createFetchAdminOrganizationTeamsQueryFn';
import { RouteListEditor } from './components/RouteListEditor';
import { isValidDomain } from './helpers/isValidDomain';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

const providerTypes: CreateSsoProviderBodyDto['type'][] = ['COGNITO_ONLY', 'AUTH0', 'WORKOS'];

const getSubmitErrorMessage = (error: unknown): string => {
  if (error instanceof ApiCallError) {
    const body = error.body as { message?: string | string[] } | undefined;
    const message = body?.message;
    if (Array.isArray(message)) {
      return message.join('. ');
    }
    if (typeof message === 'string') {
      return message;
    }
  }
  return 'Unable to save the SSO provider.';
};

export const SsoProviderModal = ({ onCancel, ssoProvider }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const queryClient = useQueryClient();
  const isEdit = ssoProvider !== undefined;

  const [identityProvider, setIdentityProvider] = useState(ssoProvider?.identityProvider ?? '');
  const [type, setType] = useState<CreateSsoProviderBodyDto['type']>(ssoProvider?.type ?? 'COGNITO_ONLY');
  const [issuerDomain, setIssuerDomain] = useState(ssoProvider?.issuerDomain ?? '');
  const [clientId, setClientId] = useState(ssoProvider?.clientId ?? '');
  const [domains, setDomains] = useState<string[]>(ssoProvider?.domains ?? []);
  const [emails, setEmails] = useState<string[]>(ssoProvider?.emails ?? []);
  const [organizationId, setOrganizationId] = useState(ssoProvider?.organizationId ?? '');
  const [defaultOrganizationRoleId, setDefaultOrganizationRoleId] = useState(
    ssoProvider?.defaultOrganizationRoleId ?? '',
  );
  const [requiresPreProvisionedUser, setRequiresPreProvisionedUser] = useState(
    ssoProvider?.requiresPreProvisionedUser ?? false,
  );
  const [trustProviderEmailVerification, setTrustProviderEmailVerification] = useState(
    ssoProvider?.trustProviderEmailVerification ?? false,
  );
  const [enforceSsoOnlyAuthentication, setEnforceSsoOnlyAuthentication] = useState(
    ssoProvider?.enforceSsoOnlyAuthentication ?? false,
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const organizationsQuery = useQuery({
    queryKey: adminOrganizationsKeys.options(),
    queryFn: createFetchOrganizationOptionsQueryFn(call),
  });

  const teamsQuery = useQuery({
    queryKey: adminOrganizationsKeys.teams(organizationId),
    queryFn: createFetchAdminOrganizationTeamsQueryFn(call),
    enabled: organizationId !== '',
  });

  const organizationOptions = useMemo(
    () =>
      (organizationsQuery.data ?? [])
        .map((org) => ({ value: org.id, label: `${org.name} (${org.slug})` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [organizationsQuery.data],
  );

  const roleOptions = useMemo(
    () =>
      (teamsQuery.data ?? [])
        .map((team) => ({ value: team.id, label: team.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [teamsQuery.data],
  );

  const saveMutation = useMutation({
    mutationFn: (requestBody: CreateSsoProviderBodyDto) =>
      isEdit
        ? call(() => SsoProviderService.update({ ssoProviderId: ssoProvider.id, requestBody }), {
            dontRedirectOnError: true,
          })
        : call(() => SsoProviderService.create({ requestBody }), { dontRedirectOnError: true }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ssoProvidersKeys.lists() });
      closeModal();
    },
    onError: (error) => {
      setSubmitError(getSubmitErrorMessage(error));
    },
  });

  const handleOrganizationChange = (_name: string, value: string) => {
    setOrganizationId(value);
    setDefaultOrganizationRoleId('');
  };

  const handleSubmit = () => {
    setSubmitError(null);
    if (!identityProvider.trim()) {
      setValidationError('Cognito identity provider name is required');
      return;
    }
    if (type === 'AUTH0' && (!issuerDomain.trim() || !clientId.trim())) {
      setValidationError('Auth0 providers need an issuer domain and a client ID for federated logout');
      return;
    }
    if ((organizationId === '') !== (defaultOrganizationRoleId === '')) {
      setValidationError('Pick both an organization and a default role, or neither');
      return;
    }
    setValidationError(null);

    saveMutation.mutate({
      identityProvider: identityProvider.trim(),
      type,
      issuerDomain: issuerDomain.trim() || undefined,
      clientId: clientId.trim() || undefined,
      domains,
      emails,
      organizationId: organizationId || null,
      defaultOrganizationRoleId: defaultOrganizationRoleId || null,
      requiresPreProvisionedUser,
      trustProviderEmailVerification,
      enforceSsoOnlyAuthentication,
    });
  };

  return (
    <ModalWrapper width="520px">
      {saveMutation.isPending ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>{isEdit ? 'Edit SSO Provider' : 'Add SSO Provider'}</ModalTitle>
      <Content>
        <div className="input-wrapper">
          <div className="label">Cognito Identity Provider</div>
          <Input
            label="Cognito Identity Provider"
            value={identityProvider}
            error={validationError !== null && !identityProvider.trim()}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIdentityProvider(e.target.value)}
          />
        </div>

        <div className="input-wrapper">
          <div className="label">Provider Type</div>
          <Select
            name="type"
            label="Provider Type"
            value={type}
            options={providerTypes.map((value) => ({ value: value ?? 'COGNITO_ONLY', label: value ?? '' }))}
            onChange={(_name, value) => setType(value as CreateSsoProviderBodyDto['type'])}
          />
        </div>

        {type === 'AUTH0' ? (
          <>
            <div className="input-wrapper">
              <div className="label">Issuer Domain</div>
              <Input
                label="Issuer Domain"
                value={issuerDomain}
                placeholder="tenant.us.auth0.com"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIssuerDomain(e.target.value)}
              />
            </div>
            <div className="input-wrapper">
              <div className="label">Client ID</div>
              <Input
                label="Client ID"
                value={clientId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientId(e.target.value)}
              />
            </div>
          </>
        ) : null}

        <RouteListEditor
          label="Domain Routes"
          values={domains}
          onChange={setDomains}
          validate={isValidDomain}
          invalidMessage="Enter a valid domain, e.g. acme.com"
          placeholder="acme.com"
        />

        <RouteListEditor
          label="Email Routes"
          values={emails}
          onChange={setEmails}
          validate={isValidEmail}
          invalidMessage="Enter a valid email address"
          placeholder="reporter@acme.com"
        />

        <div className="input-wrapper">
          <div className="label">Linked Organization (JIT users join it instead of getting a personal org)</div>
          <AutoCompleteSelect
            name="organizationId"
            label="Organization"
            value={organizationId}
            options={organizationOptions}
            onChange={handleOrganizationChange}
          />
        </div>

        {organizationId ? (
          <div className="input-wrapper">
            <div className="label">Default Role</div>
            <Select
              name="defaultOrganizationRoleId"
              label="Default Role"
              value={defaultOrganizationRoleId}
              options={roleOptions}
              onChange={(_name, value) => setDefaultOrganizationRoleId(value)}
              required
            />
          </div>
        ) : null}

        <div className="public">
          <div className="label">Require pre-provisioned user (reject unknown users at login)</div>
          <Switch
            className="switch"
            checked={requiresPreProvisionedUser}
            onChange={() => setRequiresPreProvisionedUser(!requiresPreProvisionedUser)}
          />
        </div>

        <div className="public">
          <div className="label">
            Trust provider email verification (accept logins without an email_verified claim — an IdP that mis-asserts
            emails can take over matching accounts)
          </div>
          <Switch
            className="switch"
            checked={trustProviderEmailVerification}
            onChange={() => setTrustProviderEmailVerification(!trustProviderEmailVerification)}
          />
        </div>

        <div className="public">
          <div className="label">
            Enforce SSO-only access (blocks password signup, invitations into the linked org, and self-serve workspace
            creation for managed emails)
          </div>
          <Switch
            className="switch"
            checked={enforceSsoOnlyAuthentication}
            onChange={() => setEnforceSsoOnlyAuthentication(!enforceSsoOnlyAuthentication)}
          />
        </div>

        {validationError ? <ErrorMessage className="!text-left" error={validationError} /> : null}
        {submitError ? <ErrorMessage className="!text-left" error={submitError} /> : null}

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button onClick={handleSubmit}>{isEdit ? 'Save' : 'Create'}</Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
